import * as React from 'react';

import FlatButton from 'material-ui/FlatButton';
import IconButton from 'material-ui/IconButton';
import RaisedButton from 'material-ui/RaisedButton';

import { autoprefixer } from '../../material-ui/autoprefixer';

import { Hsv, Rgb, rgbToHex, hexToRgb, rgbToHsv, hsvToRgb } from './utils';

import { Swatches } from './Swatches';
import { HsvSelector } from './HsvSelector';
import { RgbSelector } from './RgbSelector';
import { HexSelector } from './HexSelector';

import l10n from '../../util/l10n';

import KeyboardDownIcon from 'material-ui/svg-icons/hardware/keyboard-arrow-down';
import KeyboardUpIcon from 'material-ui/svg-icons/hardware/keyboard-arrow-up';

export const PREDEFINED_MODE = 1;
export const ADVANCED_MODE = 2;

const SWATCHES = [
    [ 'B71C1C', '32691E', '03579B', 'F57F18', '880E4F', '000000' ],
    [ 'D32F2F', '689F38', '0688D1', 'FBC02E', 'C2185B', '353535' ],
    [ 'F44336', '8AC34A', '09A8F4', 'FFEB3C', 'E91E63', '666666' ],
    [ 'E57373', 'ADD581', '4EC3F7', 'FFF176', 'F06292', 'A9A8A8' ],
    [ 'FFCDD2', 'DCEDC8', 'B2E5FC', 'FFF9C4', 'F8BBD0', 'FFFFFF' ]
];

interface ColorPickerProps extends React.Props<ColorPicker> {
    // `hexColor` is considered *guaranteed* to be of the form "RRGGBB" everywhere.
    hexColor: string;
    initialMode?: number;
    recentColors?: string[];
    allowDefaultColor?: boolean;
    onChange?: (hexColor: string, isDefaultColor: boolean) => void;
    onCancel?: () => void;
    onDragChange?: (isMouseDown: boolean) => void;
}

// There are two cases:
// 1. If the color is specified via the component's interface (i.e. `this.props.hexColor`),
//    or via one of the four text inputs, then `h`, `s` & `v` are derived.
// 2. If the color is specified via the HSV Selector component, then `hexColor` is derived.
// We've done this in order to prevent issues with truncation when succesively converting from
// different color spaces, issues that would have lead to an inferior user experience.
interface ColorPickerState {
    hexColor: string;
    h: number;
    s: number;
    v: number;
    activeMode: number;
}

export class ColorPicker extends React.Component<ColorPickerProps, ColorPickerState> {

    /* TODO: Extract (parts of the) initialization logic. */

    static defaultProps = {
        allowDefaultColor: true
    };

    constructor(props: ColorPickerProps) {
        super(props);

        let matchesSwatch = false;
        SWATCHES.forEach((swatches) => {
            swatches.forEach((swatchColor) => {
                if (swatchColor === props.hexColor) {
                    matchesSwatch = true;
                }
            });
        });

        this.state = {
            hexColor: props.hexColor,
            // `h === -1 && s === -1 && v === -1` means that
            // the color was either specified via the component's interface (i.e. `this.props.hexColor`),
            // or via one of the four text inputs. Hence, `h`, `s` & `v` are to be computed from RGB/hex.
            h: -1, s: -1, v: -1,
            activeMode: props.initialMode || (matchesSwatch ? PREDEFINED_MODE : ADVANCED_MODE)
        };
    }

    render() {
        const styles = this.getStyles();
        const { r, g, b } = hexToRgb(this.state.hexColor);
        const { h, s, v } =
            this.state.h !== -1 // ...` && this.state.s !== -1 && this.state.v !== -1`.
              ? this.state
              : rgbToHsv({ r, g, b})
        ;

        return (
            <div style={styles.root} onMouseDown={this.onMouseDown}>
                {
                    this.props.recentColors
                      ? (
                        <div
                            style={Object.assign({}, styles.drawer, {
                                borderBottom: 0,
                                cursor: 'auto'
                            })}
                        >
                            <span>{l10n('colorPicker.drawer.recent')}</span>
                        </div>
                      )
                      : null
                }
                {
                    this.props.recentColors
                      ? this.renderRecentColors()
                      : null
                }
                <div
                    style={styles.drawer}
                    onClick={() => this.expandDrawer(PREDEFINED_MODE)}
                    data-test-id="color-picker-drawer-predefined"
                >
                    <span>{l10n('colorPicker.drawer.predefined')}</span>
                    <IconButton>
                        {
                            this.state.activeMode === PREDEFINED_MODE
                              ? ( <KeyboardUpIcon color="#878787" /> )
                              : ( <KeyboardDownIcon color="#878787" /> )
                        }
                    </IconButton>
                    {/* TODO: Use color from some theme. */}
                </div>
                {
                    this.state.activeMode === PREDEFINED_MODE
                      ? (
                        <div style={styles.predefined}>
                            <Swatches
                                swatches={SWATCHES}
                                hexColor={this.state.hexColor}
                                dataTestIdSlug="predefined"
                                onClick={(hexColor) => this.onChangeHex(hexColor, this.onChangeHexColor)}
                            />
                            {
                                this.props.allowDefaultColor
                                  ? (
                                    <FlatButton
                                        style={styles.default}
                                        label={l10n('colorPicker.defaultColor')}
                                        onClick={() => {
                                            if (this.props.onChange) {
                                                this.props.onChange(this.state.hexColor, true);
                                            }
                                        }}
                                        data-test-id="color-picker-default"
                                    />
                                  )
                                  : null
                            }
                        </div>
                      )
                      : null
                }
                <div
                    style={styles.drawer}
                    onClick={() => this.expandDrawer(ADVANCED_MODE)}
                    data-test-id="color-picker-drawer-advanced"
                >
                    <span>{l10n('colorPicker.drawer.advanced')}</span>
                    <IconButton>
                        {
                            this.state.activeMode === ADVANCED_MODE
                              ? ( <KeyboardUpIcon color="#878787" /> )
                              : ( <KeyboardDownIcon color="#878787" /> )
                        }
                    </IconButton>
                    {/* TODO: Use color from some theme. */}
                </div>
                {
                    this.state.activeMode === ADVANCED_MODE
                      ? (
                        <div style={styles.advanced}>
                            <div
                                style={styles.swatches}
                            >
                                <div style={styles.swatchContainer}>
                                    <div style={styles.swatchLabel}>
                                      {l10n('colorPicker.swatch.current')}
                                    </div>
                                    <div
                                        style={Object.assign({}, styles.swatch, {
                                            backgroundColor: `#${this.props.hexColor}`,
                                            cursor: 'pointer'
                                        })}
                                        onClick={this.reset}
                                        data-test-id="color-picker-swatch-current"
                                    />
                                </div>
                                <div style={styles.swatchContainer}>
                                    <div style={styles.swatchLabel}>
                                        {l10n('colorPicker.swatch.new')}
                                    </div>
                                    <div
                                        style={Object.assign({}, styles.swatch, {
                                          backgroundColor: `#${this.state.hexColor}`
                                        })}
                                        data-test-id="color-picker-swatch-new"
                                    />
                                </div>
                            </div>
                            <div style={styles.selectors}>
                                <HsvSelector
                                    style={styles.hsvSelector}
                                    h={h} s={s} v={v}
                                    onChange={this.onChangeHsv}
                                    onDragChange={this.props.onDragChange}
                                />
                                <div style={styles.rgbAndHexContainer}>
                                    <RgbSelector
                                        r={r} g={g} b={b}
                                        onChange={this.onChangeRgb}
                                    />
                                    <HexSelector
                                        hexColor={this.state.hexColor}
                                        onChange={this.onChangeHex}
                                    />
                                </div>
                            </div>
                        </div>
                      )
                      : null
                }
                {
                    this.state.activeMode === ADVANCED_MODE
                      ? (
                        <div style={styles.buttons}>
                            <FlatButton
                                style={styles.cancel}
                                label={l10n('colorPicker.button.cancel')}
                                onClick={this.onCancel}
                                data-test-id="color-picker-button-cancel"
                            />
                            <RaisedButton
                                primary={true}
                                style={styles.ok}
                                label={l10n('colorPicker.button.ok')}
                                onClick={this.onChangeHexColor}
                                data-test-id="color-picker-button-ok"
                            />
                        </div>
                      )
                      : null
                }
            </div>
        );
    }

    private renderRecentColors = () => {
        const styles = this.getStyles();
        const recentColorsMatrix: string[][] = [];

        this.props.recentColors.forEach((color, index) => {
            const row = Math.floor(index / SWATCHES[0].length);

            if (row === recentColorsMatrix.length) {
                recentColorsMatrix.push([]);
            }

            recentColorsMatrix[row].push(color);
        });

        return (
            <Swatches
                swatches={recentColorsMatrix}
                hexColor={this.state.hexColor}
                style={styles.recent}
                dataTestIdSlug="recent"
                onClick={(hexColor) => this.onChangeHex(hexColor, this.onChangeHexColor)}
            />
        );
    };

    private onMouseDown = (event: React.MouseEvent<{}>) => {
        if ((event.target as HTMLElement).tagName.toLowerCase() !== 'input') {
            event.preventDefault();
        }
    };

    private expandDrawer = (mode: number) => {
        if (this.state.activeMode !== mode) {
            this.setState({ activeMode: mode } as ColorPickerState);
        }
    };

    private reset = () => {
        const { hexColor } = this.props;

        this.setState({
            hexColor,
            h: -1, s: -1, v: -1
        } as ColorPickerState);
    };

    private onCancel = () => {
        this.reset();
        if (this.props.onCancel) {
            this.props.onCancel();
        }
    };

    private onChangeHexColor = () => {
        if (this.props.onChange) {
            this.props.onChange(this.state.hexColor, false);
        }
    };

    private onChangeHsv = ({ h, s, v }: Hsv) => {
        const newRgb = hsvToRgb({ h, s, v });
        const newHex = rgbToHex(newRgb);

        this.setState({
            hexColor: newHex,
            h, s, v
        } as ColorPickerState);
    };

    private onChangeRgb = ({r, g, b}: Rgb) => {
        const newHex = rgbToHex({ r, g, b });

        this.setState({
            hexColor: newHex,
            h: -1, s: -1, v: -1
        } as ColorPickerState);
    };

    private onChangeHex = (hexColor: string, callback?: () => void) => {
        this.setState({
            hexColor,
            h: -1, s: -1, v: -1
        } as ColorPickerState, callback);
    };

    private getStyles() {
        return {
            root: Object.assign(autoprefixer({
                display: 'flex',
                flexDirection: 'column',
                userSelect: 'none' }), {
                color: '#878787', /* TODO: Use color from some theme. */
                fontSize: 13,
                lineHeight: '24px',
                backgroundColor: '#F2F4F7', /* TODO: Use color from some theme. */
                width: 312,
                overflow: 'hidden'
            }),
            drawer: Object.assign(autoprefixer({
                display: 'flex',
                justifyContent: 'space-between' }), {
                padding: '0 16px 0 20px',
                backgroundColor: '#FFFFFF', /* TODO: Use color from some theme. */
                borderBottom: '1px solid #E5E5E5', /* TODO: Use color from some theme. */
                lineHeight: '48px',
                cursor: 'pointer'
            }),
            recent: {
                padding: '0 16px 10px 20px',
                backgroundColor: '#FFFFFF', /* TODO: Use color from some theme. */
                borderBottom: '1px solid #E5E5E5', /* TODO: Use color from some theme. */
            },
            predefined: {
                margin: '8px 16px 16px 20px'
            },
            advanced: {
                margin: '8px 16px 16px 20px'
            },
            default: {
                marginTop: 6,
                width: 'calc(100% - 4px)'
            },
            swatches: Object.assign(autoprefixer({
                display: 'flex' }), {
                marginBottom: 8
            }),
            swatchContainer: {
                width: 50,
                marginRight: 6
            },
            swatchLabel: {
                width: 40,
                whiteSpace: 'nowrap'
            },
            swatch: {
                width: 50,
                height: 24,
                border: '1px solid #D9D8D8' /* TODO: Use color from some theme. */
            },
            selectedSwatch: {
                position: 'absolute',
                marginTop: -32,
                marginLeft: 16,
                pointerEvents: 'none'
            },
            selectors: autoprefixer({
                display: 'flex'
            }),
            hsvSelector: {
                marginRight: 8
            },
            rgbAndHexContainer: Object.assign(autoprefixer({
                display: 'flex',
                flexWrap: 'wrap',
                alignContent: 'space-between' }), {
                maxWidth: 110
            }),
            buttons: Object.assign(autoprefixer({
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center' }), {
                backgroundColor: '#FFFFFF', /* TODO: Use color from some theme. */
                borderTop: '1px solid #E5E5E5', /* TODO: Use color from some theme. */
                padding: '10px 16px'
            }),
            cancel: {
                marginRight: 4
            },
            ok: {
                marginRight: 4
            }
        };
    }

}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/components/color-picker/ColorPicker.tsx
 **/